// M4 – Chi tiết đơn hàng (ngăn bên phải của M3): Hàng hóa · Thanh toán · Hoàn tiền · Nhật ký
import { useState } from 'react';
import { Dropdown, Input, Modal, Tabs, message } from 'antd';
import { Link } from 'react-router-dom';
import { Ban, ChevronDown, Lock, Printer, Stethoscope, Undo2, Wallet, X } from 'lucide-react';
import { METHOD_LABEL, branchName, staffName, type PaymentLineInput } from '../data';
import usePosStore from '../store';
import { ORDER_STATUS, REFUND_STATUS, StatusPill, fmtDateTime, formatPhone, money, printReceipt, usePosAuth } from '../utils';
import PaymentModal from './PaymentModal';
import RefundModal from './RefundModal';
import PaymentSuccessModal from './PaymentSuccessModal';
import AppointmentDiff from './AppointmentDiff';
import { compareWithAppointment } from '../appointmentLink';

interface Props { orderId: string; onClose: () => void }

export default function OrderDetail({ orderId, onClose }: Props) {
  const order = usePosStore(s => s.orders.find(o => o.id === orderId));
  const transactions = usePosStore(s => s.transactions);
  const refunds = usePosStore(s => s.refunds);
  const customers = usePosStore(s => s.customers);
  const collectDebt = usePosStore(s => s.collectDebt);
  const cancelOrder = usePosStore(s => s.cancelOrder);
  const { can, userName } = usePosAuth();
  const [refundOpen, setRefundOpen] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [collected, setCollected] = useState<{ change: number } | null>(null);

  if (!order) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-slate-500">
        <p>Không tìm thấy đơn {orderId}</p>
        <button onClick={onClose} className="text-[#2c6b7f] underline">Đóng</button>
      </div>
    );
  }

  const txns = transactions.filter(t => t.orderId === order.id);
  const orderRefunds = refunds.filter(r => r.orderId === order.id);
  const customer = customers.find(c => c.id === order.customerId) || null;
  const t = order.totals;
  const st = ORDER_STATUS[order.status];
  const canPrint = can('print') && order.paid > 0 && order.status !== 'cancelled';
  const canRefund = can('create') && order.paid - order.refunded > 0 && order.status !== 'cancelled' && order.source === 'pos';
  const canCollect = can('create') && order.debt > 0 && order.status !== 'cancelled';
  const canCancel = can('delete') && order.paid === 0 && order.status !== 'cancelled';
  const paymentState = order.status === 'cancelled' ? null : order.debt > 0 ? (order.paid > 0 ? 'Thu một phần' : 'Chưa thu') : 'Đã thu đủ';

  const onCollect = async (payments: PaymentLineInput[]) => {
    await new Promise(r => setTimeout(r, 400));
    const res = collectDebt(order.id, payments, userName);
    setCollectOpen(false);
    setCollected(res);
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="shrink-0 border-b border-slate-200 px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-mono text-lg font-bold text-slate-800">{order.id}</h2>
              <StatusPill {...st} />
              {paymentState && <span className="text-xs font-medium text-slate-500">{paymentState}</span>}
            </div>
            <div className="mt-1 text-sm text-slate-500">
              {branchName(order.branchId)} · Thu ngân {order.cashier} · {fmtDateTime(order.createdAt)} · Nguồn {order.source === 'pos' ? 'POS' : 'MySpa'}
            </div>
            <div className="mt-1 text-sm">
              Khách: <b className="text-slate-800">{order.customerName}</b>
              {order.customerPhone && <span className="text-slate-500"> · {formatPhone(order.customerPhone)}</span>}
              {customer && <span className="text-slate-500"> · Hạng {customer.tier}</span>}
              {order.customerId && <Link to="/customer" className="ml-2 text-[#2c6b7f] hover:underline">Xem hồ sơ ›</Link>}
            </div>
          </div>
          <button onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Đóng"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {canCollect && (
            <button onClick={() => setCollectOpen(true)} className="flex h-10 items-center gap-1.5 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white hover:bg-[#2c6b7f]">
              <Wallet className="h-4 w-4" /> Thu nợ {money(order.debt)}đ
            </button>
          )}
          {canPrint && (
            <Dropdown menu={{ items: [{ key: 'K80', label: 'In hóa đơn K80' }, { key: 'A4', label: 'In hóa đơn A4' }], onClick: ({ key }) => printReceipt(order, key as 'K80' | 'A4') }}>
              <button className="flex h-10 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <Printer className="h-4 w-4" /> {order.printCount > 0 ? 'In lại' : 'In'} <ChevronDown className="h-4 w-4" />
              </button>
            </Dropdown>
          )}
          {canRefund && (
            <button onClick={() => setRefundOpen(true)} className="flex h-10 items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-4 text-sm font-semibold text-violet-700 hover:bg-violet-100">
              <Undo2 className="h-4 w-4" /> Hoàn tiền
            </button>
          )}
          {canCancel && (
            <button onClick={() => { setCancelReason(''); setCancelOpen(true); }} className="flex h-10 items-center gap-1.5 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 hover:bg-red-50">
              <Ban className="h-4 w-4" /> Hủy đơn
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6">
        <Tabs
          items={[
            {
              key: 'lines',
              label: 'Hàng hóa',
              children: (
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[620px] text-sm">
                      <thead className="text-xs text-slate-500">
                        <tr className="border-b border-slate-200">
                          <th className="py-2 text-left">Tên</th><th className="py-2 text-right">SL</th><th className="py-2 text-right">Đơn giá</th>
                          <th className="py-2 text-right">KM</th><th className="py-2 text-right">CK</th><th className="py-2 text-right">Thực trả</th>
                          <th className="py-2 pl-3 text-left">TV/KTV</th><th className="py-2 text-right">Đã hoàn</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.lines.map(l => (
                          <tr key={l.key} className="border-b border-slate-100 align-top">
                            <td className="py-2"><div className="font-semibold text-slate-800">{l.name}</div>{l.note && <div className="text-xs text-slate-500">Ghi chú: {l.note}</div>}</td>
                            <td className="py-2 text-right">{l.qty}</td>
                            <td className="py-2 text-right">{money(l.listPrice)}</td>
                            <td className="py-2 text-right text-green-700">{l.promoAmount ? `-${money(l.promoAmount)}` : '—'}</td>
                            <td className="py-2 text-right text-red-600">{l.lineDiscountAmount ? `-${money(l.lineDiscountAmount)}` : '—'}</td>
                            <td className="py-2 text-right font-semibold">{money(l.net)}</td>
                            <td className="py-2 pl-3">{staffName(l.staffId) || '—'}</td>
                            <td className="py-2 text-right">{l.refundedQty ? <span className="text-violet-700">{l.refundedQty} · {money(l.refundedAmount)}</span> : 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="ml-auto max-w-sm space-y-1 rounded-lg bg-slate-50 p-4 text-sm">
                    <Row label="Tạm tính" value={money(t.subtotal)} />
                    {!!t.itemPromo && <Row label="KM tự động" value={`-${money(t.itemPromo)}`} cls="text-green-700" />}
                    {!!t.lineDiscount && <Row label="Chiết khấu" value={`-${money(t.lineDiscount)}`} cls="text-red-600" />}
                    {!!t.orderPromo && <Row label={t.orderPromoName || 'KM đơn'} value={`-${money(t.orderPromo)}`} cls="text-green-700" />}
                    {!!t.orderDiscount && <Row label={`Chiết khấu đơn${order.orderDiscountInput.type === '%' ? ` ${order.orderDiscountInput.value}%` : ''}`} value={`-${money(t.orderDiscount)}`} cls="text-red-600" />}
                    {!!t.voucher && <Row label={`Voucher ${order.voucherCode}`} value={`-${money(t.voucher)}`} cls="text-red-600" />}
                    {!!t.rounding && <Row label="Làm tròn" value={money(t.rounding)} />}
                    <div className="my-1 border-t border-slate-200" />
                    <Row label="TỔNG" value={money(t.total)} cls="text-base font-bold text-[#2c6b7f]" />
                    <Row label="Đã thu" value={money(order.paid)} />
                    {!!order.refunded && <Row label="Đã hoàn" value={`-${money(order.refunded)}`} cls="text-violet-700" />}
                    {!!order.debt && <Row label="Còn nợ" value={money(order.debt)} cls="font-bold text-amber-600" />}
                    {order.approvedBy && <div className="pt-1 text-xs text-slate-500">Chiết khấu được duyệt bởi {order.approvedBy}</div>}
                  </div>
                  {order.appointment && (
                    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 text-amber-900">
                          <Stethoscope className="h-4 w-4" />
                          Lên đơn từ lịch hẹn <b className="font-mono">{order.appointment.code}</b> · tư vấn {order.appointment.consultant || '—'}
                        </span>
                        {order.appointment.path && <Link to={order.appointment.path} className="text-xs font-semibold text-[#2c6b7f] hover:underline">Xem lịch hẹn ›</Link>}
                      </div>
                      <AppointmentDiff diff={compareWithAppointment(order.appointment, order.lines, t)} posLabel="Đơn POS" defaultOpen />
                    </div>
                  )}
                  {order.note && <div className="rounded-lg border border-slate-200 p-3 text-sm"><b>Ghi chú đơn:</b> {order.note}</div>}
                  {order.cancelReason && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"><b>Lý do hủy:</b> {order.cancelReason}</div>}
                  <p className="flex items-center gap-1.5 text-xs text-slate-400"><Lock className="h-3.5 w-3.5" /> Đơn đã tạo không sửa được (BR4.1) – điều chỉnh bằng hoàn tiền hoặc hủy.</p>
                </div>
              ),
            },
            {
              key: 'payments',
              label: `Thanh toán (${txns.filter(x => x.kind !== 'refund').length})`,
              children: txns.length === 0 ? <Empty text={order.source === 'myspa' ? 'Đơn import từ MySpa không có giao dịch thu (BR9.4)' : 'Chưa có giao dịch'} /> : (
                <div className="space-y-2">
                  {txns.map(x => (
                    <div key={x.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
                      <div>
                        <div className="font-semibold text-slate-800">{x.kind === 'refund' ? 'Hoàn' : x.kind === 'debt_collection' ? 'Thu nợ' : 'Thu'} · {METHOD_LABEL[x.method]}{x.txnRef && <span className="font-mono text-xs text-slate-500"> · {x.txnRef}</span>}</div>
                        <div className="text-xs text-slate-500">{x.id} · {fmtDateTime(x.at)} · {x.cashier}{x.tendered ? ` · Khách đưa ${money(x.tendered)}, thối ${money(x.change || 0)}` : ''}</div>
                      </div>
                      <div className={`shrink-0 font-bold ${x.amount < 0 ? 'text-red-600' : 'text-slate-800'}`}>{x.amount < 0 ? '-' : ''}{money(Math.abs(x.amount))}</div>
                    </div>
                  ))}
                </div>
              ),
            },
            {
              key: 'refunds',
              label: `Hoàn tiền (${orderRefunds.length})`,
              children: orderRefunds.length === 0 ? <Empty text="Chưa có phiếu hoàn" /> : (
                <div className="space-y-2">
                  {orderRefunds.map(r => (
                    <Link key={r.id} to={`/pos/refunds?id=${r.id}`} className="block rounded-lg border border-slate-200 px-3 py-2.5 text-sm hover:border-[#3e91ab]">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono font-semibold text-slate-800">{r.id}</span>
                        <StatusPill {...REFUND_STATUS[r.status]} />
                      </div>
                      <div className="mt-1 flex justify-between text-slate-600">
                        <span>{r.lines.map(l => `${l.name} x${l.qty}`).join(', ')}</span>
                        <b className="text-violet-700">{money(r.amount)}</b>
                      </div>
                      <div className="text-xs text-slate-500">{fmtDateTime(r.createdAt)} · {r.createdBy} · {METHOD_LABEL[r.method]} · {r.reason}</div>
                    </Link>
                  ))}
                </div>
              ),
            },
            {
              key: 'logs',
              label: 'Nhật ký',
              children: (
                <ol className="relative ml-2 border-l border-slate-200">
                  {[...order.logs].reverse().map((l, i) => (
                    <li key={i} className="mb-4 ml-4">
                      <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-[#3e91ab]" />
                      <div className="text-sm text-slate-800">{l.action}</div>
                      <div className="text-xs text-slate-500">{fmtDateTime(l.at)} · {l.by}</div>
                    </li>
                  ))}
                </ol>
              ),
            },
          ]}
        />
      </div>

      <RefundModal order={refundOpen ? order : null} onClose={() => setRefundOpen(false)} />
      <PaymentModal open={collectOpen} mode="collect" amountDue={order.debt} orderCode={order.id} customer={customer} onClose={() => setCollectOpen(false)} onConfirm={onCollect} />
      <PaymentSuccessModal orderId={collected ? order.id : null} change={collected?.change || 0} onNewOrder={() => setCollected(null)} primaryLabel="XONG" />
      <Modal open={cancelOpen} title={`Hủy đơn ${order.id}`} okText="Hủy đơn" okButtonProps={{ danger: true, disabled: cancelReason.trim().length < 5 }} cancelText="Đóng"
        onCancel={() => setCancelOpen(false)} centered
        onOk={() => { cancelOrder(order.id, cancelReason.trim(), userName); setCancelOpen(false); message.success('Đã hủy đơn'); }}>
        <p className="mb-2 text-sm text-slate-600">Chỉ hủy được đơn chưa thu tiền. Lý do (tối thiểu 5 ký tự):</p>
        <Input.TextArea rows={3} maxLength={300} value={cancelReason} onChange={e => setCancelReason(e.target.value)} autoFocus />
      </Modal>
    </div>
  );
}

const Row = ({ label, value, cls = '' }: { label: string; value: string; cls?: string }) => (
  <div className={`flex justify-between gap-3 ${cls}`}><span>{label}</span><span>{value}</span></div>
);
const Empty = ({ text }: { text: string }) => <div className="py-10 text-center text-sm text-slate-400">{text}</div>;
